import { storeToRefs } from 'pinia'
import { onUnmounted, Ref, unref, watch, type MaybeRef } from 'vue'

import {
  releaseAudioOutput,
  useSetAudioOutput,
  useSpeakerMediaDeviceStore,
  useStoreMediaDevices,
} from '@/entities/media-devices'

type UseParticipantMediaPlaybackOptions = {
  /** Extra mute (hold, server mute, local self, screen tile without audio). */
  forceMuted?: MaybeRef<boolean>
}

export const useParticipantMediaPlayback = (
  mediaElement: Ref<HTMLMediaElement | undefined>,
  options: UseParticipantMediaPlaybackOptions = {},
) => {
  const { volume, isSpeakersMuted } = storeToRefs(useSpeakerMediaDeviceStore())
  const { activeSpeakerId } = storeToRefs(useStoreMediaDevices())

  const applyMuted = (element: HTMLMediaElement) => {
    element.muted = isSpeakersMuted.value || Boolean(unref(options.forceMuted))
  }

  const tryPlay = (element: HTMLMediaElement) => {
    element.play().catch((error: unknown) => {
      console.warn(error)
    })
  }

  watch([mediaElement, volume], ([element, nextVolume]) => {
    if (!element) return
    element.volume = nextVolume
  }, { immediate: true })

  watch([mediaElement, isSpeakersMuted, () => unref(options.forceMuted)], ([element]) => {
    if (!element) return
    applyMuted(element)
  }, { immediate: true })

  watch([mediaElement, activeSpeakerId], ([element, deviceId]) => {
    if (!element) return
    useSetAudioOutput(element, deviceId).catch((error: unknown) => {
      console.warn(error)
    })
  }, { immediate: true })

  watch(mediaElement, (element, previous) => {
    if (previous && previous !== element) {
      releaseAudioOutput(previous)
    }
    if (!element) return

    const onLoadedData = () => {
      tryPlay(element)
    }
    element.addEventListener('loadeddata', onLoadedData)
    // srcObject may already be set (Vue binding) before this watch runs.
    tryPlay(element)
    if (element.readyState >= HTMLMediaElement.HAVE_CURRENT_DATA) {
      tryPlay(element)
    }

    return () => {
      element.removeEventListener('loadeddata', onLoadedData)
    }
  })

  onUnmounted(() => {
    const element = mediaElement.value
    if (element) {
      releaseAudioOutput(element)
    }
  })
}
